import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { apiHosts } from "@/api/constants/apiHosts";
import distributorReportService from "@/views/distributorReport/distributorReportService";
import type { DistributorReport, DistributorReportNew } from "@/views/distributorReport/distributorReport";
import type { AxiosResponse } from "axios";
import { getDistributorById } from "../distributor/distributorApi";
import { LE } from "@/constants/enums";

const URL = "distributor-reports";
const ENTITY_NAME = "Distributor Report";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_ODR_API_BASE_URL,
};
const api = apiService<DistributorReport, Paging<DistributorReport>>(apiHosts.ODR, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {ApiOptions} config
 */
const getDistributorReports = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Int} id
 * @param {ApiOptions} config
 * @returns {api.get}
 */
const getDistributorReportById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Object} ids
 */
const getNewDistributorReport = async (ids: any) => {
  const defaultDr = { data: { ...distributorReportService.getInitialData(), ...ids } } as AxiosResponse<
    DistributorReportNew,
    any
  >;

  if (ids.distributorId) {
    await getDistributorById(ids.distributorId).then((result) => {
      defaultDr.data.distributor = result.data;
      if (result.data) {
        switch (result.data.reportFrequencyMurn) {
          case LE.DistributorReportFrequency.Monthly:
            defaultDr.data.frequencyMurn = LE.ReportFrequency.Monthly;
            break;
          case LE.DistributorReportFrequency.SemiAnnual:
            defaultDr.data.frequencyMurn = LE.ReportFrequency.SemiAnnual;
            break;
        }
      }
    });
  }

  return defaultDr;
};

/**
 * @param {DistributorReport} distributorReport
 * @param {ApiOptions} config
 */
const createDistributorReport = (distributorReport: DistributorReport, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, distributorReport, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {DistributorReport} distributorReport
 * @param {ApiOptions} config
 */
const updateDistributorReport = (distributorReport: DistributorReport, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + distributorReport.id, distributorReport, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {DistributorReport} distributorReport
 * @param {ApiOptions} config
 */
const saveDistributorReport = (distributorReport: DistributorReport, config: PagingApiAndNotifierConfig = {}) => {
  if (distributorReport.id) {
    return updateDistributorReport(distributorReport, config);
  } else {
    return createDistributorReport(distributorReport, config);
  }
};

/**
 * @param {Int} id
 * @param {ApiOptions} config
 */
const deleteDistributorReportById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteDistributorReportById,
  getDistributorReports,
  getDistributorReportById,
  getNewDistributorReport,
  createDistributorReport,
  updateDistributorReport,
  saveDistributorReport,
};
